// status-lifecycle.ts — Sprint 10: the reusable record status-lifecycle primitive.
//
// Booking proved a status state machine inline (booking-model.ts: requested → confirmed →
// completed | cancelled | no_show). Every owner↔customer category needs the same shape
// (orders, tickets, RSVPs, quotes…). This module generalizes it ONCE, adding the dimension
// booking-model lacked: WHO may perform each transition (customer vs owner vs system) +
// customer/owner display copy + an audit event for the trail.
//
// Pure, framework-free, dependency-free. It defines the contract; the gateway owner route
// (app-owner-records.ts) and future category runtimes enforce it. It does NOT replace
// booking-model.ts — BOOKING_LIFECYCLE below mirrors it exactly and a guard
// (tests/generated-app-data-model.test.mjs) asserts the two never drift.

// §PHASE11-STAFF — `staff` joins the actor list rather than reusing `provider`.
//
// `provider` was declared and never used, and it means something narrower: the practitioner a
// booking is FOR. A staff member is whoever is working the shift, which is often not the provider
// and sometimes is several people in turn. Overloading one for the other would make every future
// transition ambiguous about which of the two it meant, and the ambiguity would be discovered by
// somebody getting access they should not have.
export type LifecycleActor = 'customer' | 'owner' | 'staff' | 'provider' | 'system' | 'any';

export interface StatusState {
  id: string;
  /** copy shown to the end-user (customer-facing). */
  customerLabel: string;
  /** copy shown to the owner in the Creator workspace. */
  ownerLabel: string;
  /** a terminal state has no outgoing transitions. */
  terminal: boolean;
}

export interface StatusTransition {
  from: string;
  to: string;
  /** actors permitted to perform this transition. `any` = unrestricted. */
  by: LifecycleActor[];
}

export interface StatusLifecycle {
  id: string;
  initial: string;
  states: StatusState[];
  transitions: StatusTransition[];
}

export interface StatusEvent {
  from: string;
  to: string;
  by: LifecycleActor;
  at: number;
}

export type TransitionErrorCode =
  | 'unknown_from'
  | 'unknown_to'
  | 'terminal'
  | 'illegal_transition'
  | 'forbidden_actor';

export interface TransitionResult {
  ok: boolean;
  error?: TransitionErrorCode;
  status?: string; // the resulting status when ok
  event?: StatusEvent; // the audit event to append when ok
}

// ── Introspection ──────────────────────────────────────────────────────────────
export function lifecycleStateIds(l: StatusLifecycle): string[] {
  return l.states.map((s) => s.id);
}

export function hasState(l: StatusLifecycle, id: string): boolean {
  return l.states.some((s) => s.id === id);
}

export function isTerminal(l: StatusLifecycle, id: string): boolean {
  const s = l.states.find((x) => x.id === id);
  return !!s && s.terminal;
}

function actorAllowed(by: LifecycleActor[], actor: LifecycleActor | undefined): boolean {
  if (!actor || actor === 'any') return true; // unconstrained query
  if (by.includes('any')) return true;
  return by.includes(actor);
}

/** Can `from → to` happen, optionally restricted to `actor`? `actor` omitted/`any`
 *  answers the STATE question (ignoring who); a concrete actor adds the permission. */
export function canTransition(
  l: StatusLifecycle,
  from: string,
  to: string,
  actor?: LifecycleActor
): boolean {
  if (from === to) return false;
  if (!hasState(l, from) || !hasState(l, to)) return false;
  const t = l.transitions.find((x) => x.from === from && x.to === to);
  if (!t) return false;
  return actorAllowed(t.by, actor);
}

/** The states reachable from `from` (optionally by a given actor). */
export function allowedNext(l: StatusLifecycle, from: string, actor?: LifecycleActor): string[] {
  return l.transitions
    .filter((t) => t.from === from && actorAllowed(t.by, actor))
    .map((t) => t.to);
}

/** Validate + describe a transition. Returns the audit event to append when ok.
 *  `now` is injected (deterministic; no Date.now in shared per the workflow rules). */
export function applyTransition(
  l: StatusLifecycle,
  currentStatus: string,
  to: string,
  actor: LifecycleActor,
  now: number
): TransitionResult {
  if (!hasState(l, currentStatus)) return { ok: false, error: 'unknown_from' };
  if (!hasState(l, to)) return { ok: false, error: 'unknown_to' };
  if (isTerminal(l, currentStatus)) return { ok: false, error: 'terminal' };
  const t = l.transitions.find((x) => x.from === currentStatus && x.to === to);
  if (!t) return { ok: false, error: 'illegal_transition' };
  if (!actorAllowed(t.by, actor)) return { ok: false, error: 'forbidden_actor' };
  return { ok: true, status: to, event: { from: currentStatus, to, by: actor, at: now } };
}

// ── Pre-defined lifecycles ───────────────────────────────────────────────────────

/** BOOKING — mirrors booking-model.ts STATUS_TRANSITIONS EXACTLY (state edges).
 *  A no-drift guard asserts canTransition(BOOKING_LIFECYCLE) === isValidStatusTransition. */
export const BOOKING_LIFECYCLE: StatusLifecycle = {
  id: 'booking',
  initial: 'requested',
  states: [
    { id: 'requested', customerLabel: 'Requested', ownerLabel: 'New request', terminal: false },
    { id: 'confirmed', customerLabel: 'Confirmed', ownerLabel: 'Confirmed', terminal: false },
    // §PHASE11-STAFF — ARRIVED. Without it a receptionist has two states for a person standing in
    // front of them and a person who has not turned up yet, and they are the same state. That is the
    // gap that produces "did Sam already go in?" asked out loud across a waiting room.
    { id: 'arrived', customerLabel: 'Checked in', ownerLabel: 'Arrived', terminal: false },
    { id: 'completed', customerLabel: 'Completed', ownerLabel: 'Completed', terminal: true },
    { id: 'cancelled', customerLabel: 'Cancelled', ownerLabel: 'Cancelled', terminal: true },
    { id: 'no_show', customerLabel: 'Missed', ownerLabel: 'No-show', terminal: true },
  ],
  transitions: [
    { from: 'requested', to: 'confirmed', by: ['owner'] },
    { from: 'requested', to: 'cancelled', by: ['customer', 'owner'] },
    // Arrival, completion and no-show are the SHIFT's transitions, so staff may take them. Confirming
    // a request is not: accepting new work is a commitment about the business's capacity, and a
    // receptionist confirming a request the owner has not seen is how a diary gets double-sold.
    { from: 'confirmed', to: 'arrived', by: ['owner', 'staff'] },
    { from: 'confirmed', to: 'completed', by: ['owner'] },
    { from: 'arrived', to: 'completed', by: ['owner', 'staff'] },
    { from: 'confirmed', to: 'cancelled', by: ['customer', 'owner'] },
    { from: 'confirmed', to: 'no_show', by: ['owner', 'staff'] },
    { from: 'arrived', to: 'no_show', by: ['owner'] },
  ],
};

/** ORDER — Order-Ahead. NOTE: preparing/ready ADVANCE only with server sync (GAP-SERVER/NOTIFY,
 *  not wired) — the lifecycle DEFINES them, but the honest v1 runtime must keep status at
 *  `placed` and never auto-advance/fake `ready` until live status is wired. */
export const ORDER_LIFECYCLE: StatusLifecycle = {
  id: 'order',
  initial: 'placed',
  states: [
    { id: 'placed', customerLabel: 'Placed', ownerLabel: 'New order', terminal: false },
    // §PHASE11-STAFF — ACCEPTED is the moment a human took the order on. A customer watching
    // "Placed" for six minutes cannot tell whether the shop has seen it or the shop is closed, and
    // the difference is the whole reason they are watching.
    { id: 'accepted', customerLabel: 'Accepted', ownerLabel: 'Accepted', terminal: false },
    { id: 'preparing', customerLabel: 'Preparing', ownerLabel: 'Preparing', terminal: false },
    { id: 'ready', customerLabel: 'Ready', ownerLabel: 'Ready for pickup', terminal: false },
    // §PHASE11-STAFF — the FULFILMENT arm, for a shop that ships rather than hands over a counter.
    // These live in the same lifecycle because they are the same record; which arm a category may
    // take is bound by STAFF_ACTIONS, not by having two lifecycles that would drift.
    { id: 'picking', customerLabel: 'Being picked', ownerLabel: 'Picking', terminal: false },
    { id: 'packed', customerLabel: 'Packed', ownerLabel: 'Packed', terminal: false },
    { id: 'shipped', customerLabel: 'On its way', ownerLabel: 'Shipped', terminal: false },
    { id: 'completed', customerLabel: 'Completed', ownerLabel: 'Completed', terminal: true },
    { id: 'cancelled', customerLabel: 'Cancelled', ownerLabel: 'Cancelled', terminal: true },
  ],
  transitions: [
    { from: 'placed', to: 'accepted', by: ['owner', 'staff'] },
    { from: 'placed', to: 'preparing', by: ['owner'] },
    { from: 'placed', to: 'cancelled', by: ['customer', 'owner'] },
    { from: 'accepted', to: 'preparing', by: ['owner', 'staff'] },
    { from: 'accepted', to: 'picking', by: ['owner', 'staff'] },
    { from: 'accepted', to: 'cancelled', by: ['owner'] },
    { from: 'preparing', to: 'ready', by: ['owner', 'staff'] },
    { from: 'preparing', to: 'cancelled', by: ['owner'] },
    { from: 'ready', to: 'completed', by: ['owner', 'staff'] },
    { from: 'placed', to: 'picking', by: ['owner', 'staff'] },
    { from: 'picking', to: 'packed', by: ['owner', 'staff'] },
    { from: 'picking', to: 'cancelled', by: ['owner'] },
    { from: 'packed', to: 'shipped', by: ['owner', 'staff'] },
    { from: 'shipped', to: 'completed', by: ['owner', 'system'] },
  ],
};

/** TICKET — service requests / ticketing. */
export const TICKET_LIFECYCLE: StatusLifecycle = {
  id: 'ticket',
  initial: 'open',
  states: [
    { id: 'open', customerLabel: 'Open', ownerLabel: 'Open', terminal: false },
    { id: 'in_progress', customerLabel: 'In progress', ownerLabel: 'In progress', terminal: false },
    { id: 'resolved', customerLabel: 'Resolved', ownerLabel: 'Resolved', terminal: false },
    { id: 'closed', customerLabel: 'Closed', ownerLabel: 'Closed', terminal: true },
    { id: 'cancelled', customerLabel: 'Cancelled', ownerLabel: 'Cancelled', terminal: true },
  ],
  transitions: [
    // §PHASE11-STAFF — an agent claims a ticket and resolves it. CLOSING is deliberately NOT theirs:
    // closed is terminal, and the person who decides a problem is finished is the customer who had
    // it or the owner who answers for it. An agent closing their own resolution is how a queue gets
    // clean and a customer stays unhelped.
    { from: 'open', to: 'in_progress', by: ['owner', 'staff'] },
    { from: 'open', to: 'cancelled', by: ['customer', 'owner'] },
    { from: 'in_progress', to: 'resolved', by: ['owner', 'staff'] },
    { from: 'in_progress', to: 'cancelled', by: ['owner'] },
    { from: 'resolved', to: 'closed', by: ['customer', 'owner'] },
    { from: 'resolved', to: 'in_progress', by: ['owner', 'staff'] },
  ],
};

/** RSVP — events. A guest sets their own attendance; no owner-driven flow. */
export const RSVP_LIFECYCLE: StatusLifecycle = {
  id: 'rsvp',
  initial: 'going',
  states: [
    { id: 'going', customerLabel: 'Going', ownerLabel: 'Going', terminal: false },
    { id: 'maybe', customerLabel: 'Maybe', ownerLabel: 'Maybe', terminal: false },
    { id: 'declined', customerLabel: 'Not going', ownerLabel: 'Declined', terminal: false },
  ],
  transitions: [
    { from: 'going', to: 'maybe', by: ['customer'] },
    { from: 'going', to: 'declined', by: ['customer'] },
    { from: 'maybe', to: 'going', by: ['customer'] },
    { from: 'maybe', to: 'declined', by: ['customer'] },
    { from: 'declined', to: 'going', by: ['customer'] },
    { from: 'declined', to: 'maybe', by: ['customer'] },
  ],
};

/** QUOTE — estimators. */
export const QUOTE_LIFECYCLE: StatusLifecycle = {
  id: 'quote',
  initial: 'draft',
  states: [
    { id: 'draft', customerLabel: 'Draft', ownerLabel: 'Draft', terminal: false },
    { id: 'sent', customerLabel: 'Received', ownerLabel: 'Sent', terminal: false },
    { id: 'accepted', customerLabel: 'Accepted', ownerLabel: 'Accepted', terminal: true },
    { id: 'declined', customerLabel: 'Declined', ownerLabel: 'Declined', terminal: true },
    { id: 'expired', customerLabel: 'Expired', ownerLabel: 'Expired', terminal: true },
  ],
  transitions: [
    { from: 'draft', to: 'sent', by: ['owner'] },
    { from: 'sent', to: 'accepted', by: ['customer'] },
    { from: 'sent', to: 'declined', by: ['customer', 'owner'] },
    { from: 'sent', to: 'expired', by: ['system', 'owner'] },
  ],
};

/** PAYMENT — the MANUAL (no auto-verify) payment status, matching the gateway's
 *  ALLOWED_PAYMENT_STATUS (app-payments.ts). There is NO machine-verified `paid`. */
export const PAYMENT_LIFECYCLE: StatusLifecycle = {
  id: 'payment',
  initial: 'not_required',
  states: [
    { id: 'not_required', customerLabel: 'No payment needed', ownerLabel: 'No payment', terminal: false },
    { id: 'awaiting_payment', customerLabel: 'Awaiting payment', ownerLabel: 'Awaiting payment', terminal: false },
    { id: 'external_pending', customerLabel: 'Payment pending', ownerLabel: 'External pending', terminal: false },
    { id: 'pay_at_business_pending', customerLabel: 'Pay at business', ownerLabel: 'Pay at business', terminal: false },
    { id: 'marked_paid', customerLabel: 'Paid', ownerLabel: 'Marked paid (manual)', terminal: false },
    { id: 'payment_canceled', customerLabel: 'Payment canceled', ownerLabel: 'Canceled', terminal: true },
    { id: 'refunded_manual', customerLabel: 'Refunded', ownerLabel: 'Refunded (manual)', terminal: true },
  ],
  transitions: [
    { from: 'awaiting_payment', to: 'marked_paid', by: ['owner'] },
    { from: 'awaiting_payment', to: 'payment_canceled', by: ['owner'] },
    { from: 'external_pending', to: 'marked_paid', by: ['owner'] },
    { from: 'external_pending', to: 'payment_canceled', by: ['owner'] },
    { from: 'pay_at_business_pending', to: 'marked_paid', by: ['owner'] },
    { from: 'pay_at_business_pending', to: 'payment_canceled', by: ['owner'] },
    { from: 'marked_paid', to: 'refunded_manual', by: ['owner'] },
  ],
};

// §Batch 2d — waitlist (a SEPARATE collection, NOT a booking status). A customer joins with
// 'new'; only the OWNER moves it (contacted / promoted_candidate / dismissed). promoted_candidate
// is just a flag the owner sets — it does NOT auto-create a booking/order (that becomes real only
// once reserve_slot is wired). Every transition is owner-only.
export const WAITLIST_LIFECYCLE: StatusLifecycle = {
  id: 'waitlist',
  initial: 'new',
  states: [
    { id: 'new', customerLabel: 'On the waitlist', ownerLabel: 'New', terminal: false },
    { id: 'contacted', customerLabel: 'Contacted', ownerLabel: 'Contacted', terminal: false },
    { id: 'promoted_candidate', customerLabel: 'Offered a spot', ownerLabel: 'Promote candidate', terminal: false },
    { id: 'dismissed', customerLabel: 'Closed', ownerLabel: 'Dismissed', terminal: true },
  ],
  transitions: [
    { from: 'new', to: 'contacted', by: ['owner'] },
    { from: 'new', to: 'promoted_candidate', by: ['owner'] },
    { from: 'new', to: 'dismissed', by: ['owner'] },
    { from: 'contacted', to: 'promoted_candidate', by: ['owner'] },
    { from: 'contacted', to: 'dismissed', by: ['owner'] },
    { from: 'promoted_candidate', to: 'contacted', by: ['owner'] },
    { from: 'promoted_candidate', to: 'dismissed', by: ['owner'] },
  ],
};

// §Owner-console generalization — INVENTORY item availability. The stock state an OWNER controls
// for a catalog item. in_stock/low_stock/out_of_stock are freely interchangeable (a restock or a sale
// moves either way); discontinued is a terminal retire. Owner-only — a customer never mutates stock.
export const INVENTORY_LIFECYCLE: StatusLifecycle = {
  id: 'inventory',
  initial: 'in_stock',
  states: [
    { id: 'in_stock', customerLabel: 'In stock', ownerLabel: 'In stock', terminal: false },
    { id: 'low_stock', customerLabel: 'Low stock', ownerLabel: 'Low stock', terminal: false },
    { id: 'out_of_stock', customerLabel: 'Out of stock', ownerLabel: 'Out of stock', terminal: false },
    { id: 'discontinued', customerLabel: 'Discontinued', ownerLabel: 'Discontinued', terminal: true },
  ],
  transitions: [
    { from: 'in_stock', to: 'low_stock', by: ['owner'] },
    { from: 'in_stock', to: 'out_of_stock', by: ['owner'] },
    { from: 'in_stock', to: 'discontinued', by: ['owner'] },
    { from: 'low_stock', to: 'in_stock', by: ['owner'] },
    { from: 'low_stock', to: 'out_of_stock', by: ['owner'] },
    { from: 'low_stock', to: 'discontinued', by: ['owner'] },
    { from: 'out_of_stock', to: 'in_stock', by: ['owner'] },
    { from: 'out_of_stock', to: 'low_stock', by: ['owner'] },
    { from: 'out_of_stock', to: 'discontinued', by: ['owner'] },
  ],
};

// §Owner-console generalization — LEAVE (HR time-off approve/deny). An employee's request the OWNER
// (manager) approves or denies; either party may cancel a still-open request. Honest: there is no
// fabricated auto-approve — the owner must act. A record created without a status starts at
// `requested` (the initial), so the owner always has a real pending item to approve/deny.
export const LEAVE_LIFECYCLE: StatusLifecycle = {
  id: 'leave',
  initial: 'requested',
  states: [
    { id: 'requested', customerLabel: 'Requested', ownerLabel: 'Pending', terminal: false },
    { id: 'approved', customerLabel: 'Approved', ownerLabel: 'Approved', terminal: false },
    { id: 'denied', customerLabel: 'Denied', ownerLabel: 'Denied', terminal: true },
    { id: 'cancelled', customerLabel: 'Cancelled', ownerLabel: 'Cancelled', terminal: true },
  ],
  transitions: [
    { from: 'requested', to: 'approved', by: ['owner'] },
    { from: 'requested', to: 'denied', by: ['owner'] },
    { from: 'requested', to: 'cancelled', by: ['customer', 'owner'] },
    { from: 'approved', to: 'cancelled', by: ['customer', 'owner'] },
  ],
};

// §Owner-console generalization — LISTING (directory owner-approve). A listing is submitted/authored
// as `pending`; the OWNER approves (it becomes browsable) or rejects it, and may later archive an
// approved listing. Only the owner moves it — approval is never fabricated. Runtime note: a `pending`
// or `rejected` listing must be withheld from the public browse surface (recipe-side filter).
export const LISTING_LIFECYCLE: StatusLifecycle = {
  id: 'listing',
  initial: 'pending',
  states: [
    { id: 'pending', customerLabel: 'Pending review', ownerLabel: 'Pending', terminal: false },
    { id: 'approved', customerLabel: 'Listed', ownerLabel: 'Approved', terminal: false },
    { id: 'rejected', customerLabel: 'Not approved', ownerLabel: 'Rejected', terminal: true },
    { id: 'archived', customerLabel: 'Archived', ownerLabel: 'Archived', terminal: true },
  ],
  transitions: [
    { from: 'pending', to: 'approved', by: ['owner'] },
    { from: 'pending', to: 'rejected', by: ['owner'] },
    { from: 'approved', to: 'archived', by: ['owner'] },
    { from: 'approved', to: 'pending', by: ['owner'] },
  ],
};

/** DEAL — the CRM pipeline (Lead→Qualified→Proposal→Won|Lost). Owner-only; forward-only advance with a
 *  Lost escape from every active stage; Won and Lost terminal. Defined HERE (the canonical lifecycle
 *  layer) so the registry owns it and crm-deal-lifecycle.ts CONSUMES it (feature→shared, no inversion,
 *  no barrel collision — that module never re-exports this). State ids are the EXISTING capitalized
 *  stage values every writer already stores (a lowercase convention would make applyTransition reject
 *  every persisted deal as unknown_from). */
export const DEAL_LIFECYCLE: StatusLifecycle = {
  id: 'deal',
  initial: 'Lead',
  states: [
    { id: 'Lead', customerLabel: 'Lead', ownerLabel: 'Lead', terminal: false },
    { id: 'Qualified', customerLabel: 'Qualified', ownerLabel: 'Qualified', terminal: false },
    { id: 'Proposal', customerLabel: 'Proposal', ownerLabel: 'Proposal', terminal: false },
    { id: 'Won', customerLabel: 'Won', ownerLabel: 'Won', terminal: true },
    { id: 'Lost', customerLabel: 'Lost', ownerLabel: 'Lost', terminal: true },
  ],
  transitions: [
    { from: 'Lead', to: 'Qualified', by: ['owner'] },
    { from: 'Lead', to: 'Lost', by: ['owner'] },
    { from: 'Qualified', to: 'Proposal', by: ['owner'] },
    { from: 'Qualified', to: 'Lost', by: ['owner'] },
    { from: 'Proposal', to: 'Won', by: ['owner'] },
    { from: 'Proposal', to: 'Lost', by: ['owner'] },
  ],
};

export const LIFECYCLES: Record<string, StatusLifecycle> = {
  booking: BOOKING_LIFECYCLE,
  order: ORDER_LIFECYCLE,
  ticket: TICKET_LIFECYCLE,
  rsvp: RSVP_LIFECYCLE,
  quote: QUOTE_LIFECYCLE,
  payment: PAYMENT_LIFECYCLE,
  waitlist: WAITLIST_LIFECYCLE,
  inventory: INVENTORY_LIFECYCLE,
  leave: LEAVE_LIFECYCLE,
  listing: LISTING_LIFECYCLE,
  deal: DEAL_LIFECYCLE, // TRACK C — CRM pipeline (Lead→Qualified→Proposal→Won|Lost), owner-only
};

export function getLifecycle(id: string): StatusLifecycle | null {
  return LIFECYCLES[id] ?? null;
}
